// Live delivery side of the Measurement Core (v1.3.12 §§6.3, 7, 8.1).
//
// The backend confirms business outcomes (a league join, a fill, an activated
// account) and returns each one in its API response as
//   { name, params, event_instance_id, destinations }.
// This module hands that event to whichever measurement path owns the current
// surface, then tells the backend what happened so the shared outbox does not
// send it a second time:
//
//   browser   dataLayer -> GTM -> GA4                     ack { ga4_web }
//   native    'Qushi' bridge -> Firebase / AppsFlyer       ack { firebase, appsflyer }
//   legacy / unclassified   nothing is sent and nothing acked; the outbox
//                           decides (Core §3.2: never fall back to the browser)
//
// It also gives the native shell a measurement context on every load, so the
// shell can post its install identifiers (§6.3), and keeps the backend's
// consent ledger in step with the visitor's choice (§8.1).
//
// Calls go through plain axios, NOT the shared axiosInstance: that instance logs
// the user out on any 401, and a measurement request must never be able to end
// someone's session.
//
// Nothing here throws. Measurement must never break the screen it measures.
import axios from "axios";
import { API_BASE_URL } from "../config/api";

const CONTEXT_KEY = "qushi_measurement_context";
// Whose context it is: the signed-in user id when it was made ("" signed out).
const CONTEXT_OWNER_KEY = "qushi_measurement_context_owner";
const DELIVERED_KEY = "qushi_measurement_delivered";

const surface = () => window.QushiSurface?.surface || "browser";
const isNative = () => surface() === "native";
const isBrowser = () => surface() === "browser";

// The surface value the backend stores: the app reports its own platform.
const syncSurface = () => (isNative() ? window.QushiSurface?.platform : "browser");

const authHeaders = (token = null) => {
  try {
    const t = token || JSON.parse(localStorage.getItem("accessToken"));
    return t ? { Authorization: "Bearer " + t } : {};
  } catch (e) {
    return {};
  }
};

const post = (path, body, token = null) =>
  axios.post(API_BASE_URL + path, body, {
    headers: { "Content-Type": "application/json", ...authHeaders(token) },
  });

// The bridge only takes calls once the plugin has announced it is ready. The
// ready event can fire before this listener exists, so the handler being
// present also counts, a poll catches a missed event, and a timeout lets the
// call go ahead (and fail visibly to the caller) instead of waiting forever.
const bridgeReady = () =>
  // eslint-disable-next-line no-undef
  (typeof isFlutterInAppWebViewReady !== "undefined" && isFlutterInAppWebViewReady) ||
  typeof window.flutter_inappwebview?.callHandler === "function";

const whenBridgeReady = () =>
  new Promise((resolve) => {
    if (bridgeReady()) {
      resolve();
      return;
    }
    let poll;
    let timer;
    const done = () => {
      clearInterval(poll);
      clearTimeout(timer);
      window.removeEventListener("flutterInAppWebViewPlatformReady", done);
      resolve();
    };
    window.addEventListener("flutterInAppWebViewPlatformReady", done, { once: true });
    poll = setInterval(() => bridgeReady() && done(), 100);
    timer = setTimeout(done, 5000);
  });

const callBridge = async (message) => {
  await whenBridgeReady();
  return window.flutter_inappwebview.callHandler("Qushi", message);
};

// ---------------------------------------------------------------------------
// Context and consent

export const getMeasurementContextId = () => {
  try {
    return sessionStorage.getItem(CONTEXT_KEY);
  } catch (e) {
    return null;
  }
};

// The web stream's cookie suffix: GA4 keeps the session in _ga_<id without "G-">.
const GA4_COOKIE_SUFFIX = "WSL0X9MCMV";

const readCookie = (name) => {
  try {
    const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : null;
  } catch (e) {
    return null;
  }
};

/**
 * The browser's own GA4 identity, read from the cookies GA sets. Under denied
 * analytics consent GA sets none, so nothing is read: QUSHi never makes up a
 * client_id (Core §8.1).
 *   _ga               GA1.1.<client_id: two numbers>
 *   _ga_WSL0X9MCMV    GS1.1.<session_id>.…   or   GS2.1.s<session_id>$o…
 */
const gaIdentity = () => {
  const out = {};
  const ga = readCookie("_ga");
  const parts = ga ? ga.split(".") : [];
  if (parts.length >= 4) {
    out["X-GA-Client-Id"] = parts.slice(-2).join(".");
  }
  const session = readCookie("_ga_" + GA4_COOKIE_SUFFIX);
  const id = session
    ? session.startsWith("GS2.")
      ? (session.split(".")[2] || "").split("$")[0].replace(/^s/, "")
      : session.split(".")[2]
    : null;
  if (id && /^\d+$/.test(id)) {
    out["X-GA-Session-Id"] = id;
  }
  return out;
};

/**
 * Sent with every API call: this page's measurement context, so the backend
 * ties an occurrence (and a signup or order origin) to it, plus the browser's
 * GA4 ids, which the outbox needs to send a deferred event to the same
 * browser. Empty on legacy and unclassified surfaces.
 */
const currentUserId = () => {
  try {
    return localStorage.getItem("user_id") || "";
  } catch (e) {
    return "";
  }
};

const readOwner = () => {
  try {
    return sessionStorage.getItem(CONTEXT_OWNER_KEY) || "";
  } catch (e) {
    return "";
  }
};

const writeOwner = (owner) => {
  try {
    sessionStorage.setItem(CONTEXT_OWNER_KEY, owner);
  } catch (e) { }
};

/**
 * A context belongs to the account signed in when it was made, or to nobody
 * when made signed out (the backend gives it to the first account that signs
 * in or signs up on it). After a sign-out, or a sign-in as someone else in the
 * same tab, the old one belongs to the wrong account and the backend rightly
 * ignores it, so a fresh context is fetched instead.
 */
const contextIsCurrent = () => {
  const owner = readOwner();
  const user = currentUserId();
  if (owner === user) return true;
  if (owner === "") {
    // An anonymous context now in use by the account that signed in on it.
    writeOwner(user);
    return true;
  }
  initMeasurement();
  return false;
};

export const measurementContextHeader = () => {
  try {
    const headers = {};
    const id = getMeasurementContextId();
    if (!id || !contextIsCurrent()) return headers;
    headers["X-Measurement-Context"] = id;
    if (isBrowser()) Object.assign(headers, gaIdentity());
    return headers;
  } catch (e) {
    return {};
  }
};

/**
 * Once per page load, and again whenever the signed-in account changes, on the
 * browser and the official app (never on legacy or unclassified surfaces): get
 * a fresh context, give it to the shell, and record the current consent
 * against it.
 */
let initInFlight = null;

export const initMeasurement = () => {
  if (!initInFlight) {
    initInFlight = createContext().finally(() => {
      initInFlight = null;
    });
  }
  return initInFlight;
};

const createContext = async () => {
  try {
    if (!isBrowser() && !isNative()) return;

    const owner = currentUserId();
    const res = await post("measurement/context", { surface: syncSurface() });
    const contextId = res?.data?.success ? res.data.data?.context_id : null;
    if (!contextId) return;

    try {
      sessionStorage.setItem(CONTEXT_KEY, contextId);
    } catch (e) { }
    writeOwner(owner);

    if (isNative()) {
      callBridge({ event: "measurement_context", data: { context_id: contextId } }).catch(() => { });
    }
    await syncConsent();
    await syncAttribution();
  } catch (e) {
    // Measurement is best effort.
  }
};

/**
 * §8.1 consent_sync: the visitor's current Consent Mode states, as the privacy
 * store computes them for the tags, so the backend and the page never disagree.
 */
export const syncConsent = async () => {
  try {
    const P = window.QushiPrivacy;
    const contextId = getMeasurementContextId();
    if (!P || (!contextId && !authHeaders().Authorization)) return;
    if (!isBrowser() && !isNative()) return;

    const prefs = P.read();
    const state = P.consentState(prefs);

    await post("measurement/consent-sync", {
      context_id: contextId,
      surface: syncSurface(),
      policy_version: "v" + (prefs?.v || 1),
      region_policy: P.isPriorConsentRegion() ? "eea_uk_ch" : "rest_of_world",
      consent_updated_at: prefs?.updatedAt || Date.now(),
      // Left unset by the store only where analytics is on by default.
      analytics_storage: state.analytics_storage || "granted",
      ad_storage: state.ad_storage,
      ad_user_data: state.ad_user_data,
      ad_personalization: state.ad_personalization,
      gpc: navigator.globalPrivacyControl === true,
      sale_share_opt_out: P.adOptOut(prefs),
    });
  } catch (e) {
    // Best effort; the outbox treats a missing snapshot as unverifiable consent.
  }
};

/**
 * Ad Spec v1.0.12 §3.2: the ad platforms' click references for this page's
 * context (privacy-store.js keeps them, only under advertising consent), so
 * the outbox's Conversions API routes can match a conversion to its ad click.
 * Without advertising consent an empty report goes instead, with
 * ads_allowed: false, which deletes what is kept for this context and, once
 * the context belongs to an account, for every context of that account.
 * Browser only: the app's installs reach the platforms through AppsFlyer.
 */
export const syncAttribution = async () => {
  try {
    if (!isBrowser()) return;
    const P = window.QushiPrivacy;
    const contextId = getMeasurementContextId();
    if (!P || !contextId || typeof P.readAttribution !== "function") return;

    await post("measurement/attribution", {
      context_id: contextId,
      ...P.readAttribution(),
      // A withdrawn choice, as opposed to a page with no click references.
      ...(typeof P.adsAllowedNow === "function"
        ? { ads_allowed: P.adsAllowedNow() }
        : {}),
      // Scheme, host and path only: never the query string.
      page_url: window.location.origin + window.location.pathname,
    });
  } catch (e) {
    // Best effort: without references the server routes record no_match_context.
  }
};

// ---------------------------------------------------------------------------
// Client-intent events

/**
 * An event only the live client knows about (Core §6.5 "Active client", e.g.
 * order_started): no backend record, no ack, no outbox. Browser → dataLayer →
 * GTM → GA4; official app → the bridge as a client_event, which the shell logs
 * to Firebase only. Consent is the client's own gate: Consent Mode in the
 * browser, the native SDK's consent state in the app. Legacy and unclassified
 * surfaces send nothing.
 */
export const trackClientEvent = (name, params = {}) => {
  try {
    const clean = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== undefined && value !== null)
    );
    if (isBrowser()) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...clean });
    } else if (isNative()) {
      callBridge({ event: "client_event", name, params: clean }).catch(() => { });
    }
  } catch (e) { }
};

// ---------------------------------------------------------------------------
// Backend-confirmed events

const alreadyDelivered = (id) => {
  try {
    return (JSON.parse(sessionStorage.getItem(DELIVERED_KEY)) || []).includes(id);
  } catch (e) {
    return false;
  }
};

const markDelivered = (id) => {
  try {
    const ids = JSON.parse(sessionStorage.getItem(DELIVERED_KEY)) || [];
    ids.push(id);
    sessionStorage.setItem(DELIVERED_KEY, JSON.stringify(ids.slice(-100)));
  } catch (e) { }
};

const deliverOne = async (ev, token) => {
  if (!ev?.name || !ev?.event_instance_id || alreadyDelivered(ev.event_instance_id)) return;
  // Marked before sending: a rerender or a second response for the same
  // occurrence must never produce a second send.
  markDelivered(ev.event_instance_id);

  let results = null;
  if (isBrowser()) {
    window.dataLayer = window.dataLayer || [];
    // event_id is the occurrence id, for the ad pixels' deduplication against
    // their server routes (Ad Spec v1.0.12 §3.2: pixel eventID = server
    // event_id). It is never a GA4 parameter: the GA4 tags list their own
    // parameters and do not include it (§6).
    window.dataLayer.push({ event: ev.name, ...(ev.params || {}), event_id: ev.event_instance_id });
    results = { ga4_web: "logged" };
  } else if (isNative()) {
    results = await callBridge({
      event: "measurement_event",
      name: ev.name,
      params: ev.params || {},
      event_instance_id: ev.event_instance_id,
      destinations: ev.destinations || [],
    });
  }

  if (results && typeof results === "object") {
    await post("measurement/ack", { event_instance_id: ev.event_instance_id, results }, token);
  }
};

/**
 * Deliver the confirmed events an API response carried (its top-level
 * `measurement_events`). Call it with the raw response data; anything without
 * events is ignored. A sign-in response carries the new session's token, which
 * the ack uses because the page has not stored it yet.
 */
export const deliverConfirmedEvents = (responseData) => {
  try {
    const events = responseData?.measurement_events;
    if (!Array.isArray(events)) return;
    const token = responseData?.data?.access_token || null;
    events.forEach((ev) => {
      deliverOne(ev, token).catch(() => { });
    });
  } catch (e) { }
};
